import { History, UnregisterCallback, Location, Pathname } from 'history';

export type GA = (...args: any[]) => void;

export const transformPathnameToPage = (pathname: string) => {
  if (/^\/renewpassword\/[^/]+$/.test(pathname)) {
    return '/renewpassword';
  }
  if (/^\/venue\/[^/]+\/?$/.test(pathname)) {
    return '/venue';
  }
  if (/^\/venue\/.+\/access\/app-passwords\/?$/.test(pathname)) {
    return '/venue/access/app-passwords';
  }
  if (/^\/venue\/.+\/access\/user-management\/?$/.test(pathname)) {
    return '/venue/access/user-management';
  }
  if (/^\/venue\/.+\/details\/?$/.test(pathname)) {
    return '/venue/details';
  }
  if (/^\/venue\/.+\/purchases\/?$/.test(pathname)) {
    return '/venue/purchases';
  }
  if (/^\/venue\/.+\/purchases-v2\/?$/.test(pathname)) {
    return '/venue/purchases-v2';
  }
  const PURCHASES_V2_SUBPAGE_PATTERN = /^\/venue\/.+\/purchases-v2(\/.+)$/;
  const purchasesV2SubpageMatch = new RegExp(PURCHASES_V2_SUBPAGE_PATTERN).exec(pathname);
  if (purchasesV2SubpageMatch !== null) {
    return `/venue/purchases-v2${purchasesV2SubpageMatch[1]}`;
  }
  if (/^\/venue\/.+\/menu-editor\/?$/.test(pathname)) {
    return '/venue/menu-editor';
  }
  if (/^\/venue\/.+\/menu-editor\/options\/?$/.test(pathname)) {
    return '/venue/menu-editor/options';
  }
  if (/^\/venue\/.+\/menu-editor\/[^/]+\/?$/.test(pathname)) {
    return '/venue/menu-editor/category';
  }
  if (/^\/venue\/.+\/payouts\/?$/.test(pathname)) {
    return '/venue/payouts';
  }
  if (/^\/venue\/.+\/translation\/?$/.test(pathname)) {
    return '/venue/translation';
  }
  if (/^\/merchant\/[^/]+\/?$/.test(pathname)) {
    return '/merchant';
  }
  if (/^\/merchant\/.+\/access\/user-management\/?$/.test(pathname)) {
    return '/merchant/access/user-management';
  }
  if (/^\/merchant\/.+\/access\/api-access\/?$/.test(pathname)) {
    return '/merchant/access/api-access';
  }
  if (/^\/venue-group\/[^/]+\/?$/.test(pathname)) {
    return '/venue-group';
  }
  if (/^\/invite\/[^/]+$/.test(pathname)) {
    return '/invite';
  }
  if (/^\/ipad\/login\/[^/]+\/[^/]+$/.test(pathname)) {
    return '/ipad/login';
  }
  return pathname;
};

class GaPageviewTracker {
  history: History;
  getGa: () => GA;

  currentPathname: Pathname | null = null;

  unlistenHistory: UnregisterCallback | null = null;

  constructor(history: History, getGa: () => GA) {
    this.history = history;
    this.getGa = getGa;
  }

  start() {
    this.unlistenHistory = this.history.listen(this.handleLocationChange);
    this.currentPathname = this.history.location.pathname;

    this.sendGaPageview();
  }

  stop() {
    if (this.unlistenHistory !== null) {
      this.unlistenHistory();
    }
  }

  sendGaPageview() {
    const ga = this.getGa();
    const page = this.currentPathname !== null ? transformPathnameToPage(this.currentPathname) : null;
    ga('send', 'pageview', page);
  }

  handleLocationChange = (location: Location) => {
    if (this.currentPathname !== location.pathname) {
      this.currentPathname = location.pathname;
      this.sendGaPageview();
    }
  };
}

export const loadGaScript = () => {
  /**
   * Sets up `window.ga` with a command queue and loads analytics.js script asynchronously
   * as explained in https://developers.google.com/analytics/devguides/collection/analyticsjs/
   *
   * If window.ga is already set, it doesn't touch it and returns false. Otherwise returns true.
   */

  if (window.ga) {
    return false;
  }

  window.ga = (...args: any[]) => window.ga.q.push(args);
  window.ga.q = [];
  window.ga.l = new Date().valueOf();

  window.GoogleAnalyticsObject = 'ga';

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.google-analytics.com/analytics.js';

  const [head] = document.getElementsByTagName('head');
  head.appendChild(script);

  return true;
};

let gaPageviewTracker: GaPageviewTracker | undefined;

export const getGa = (): GA | undefined => window.ga;

export const init = (history: History) => {
  if (gaPageviewTracker !== undefined) {
    console.warn('Google Analytics has already been initialised');
    return;
  }

  if (process.env.GA_TRACKING_CODE) {
    if (!loadGaScript()) {
      console.warn('`window.ga` was already set');
    }

    window.ga('create', process.env.GA_TRACKING_CODE, 'auto');
    gaPageviewTracker = new GaPageviewTracker(history, () => window.ga);
    gaPageviewTracker.start();
  }
};

export const gaSetCustomUserDimensions = (isUserLoggedIn: boolean, isUserEmployee: boolean) => {
  const ga = getGa();

  if (ga) {
    ga('set', {
      dimension1: isUserLoggedIn ? 'yes' : 'no',
      dimension2: isUserEmployee ? 'yes' : 'no',
      dimension3: 'no', // 'no' for dimension3 means user is not using Merchant App
    });
  }
};
